import clsx from 'clsx';
import { useCallback, useMemo } from 'react';
import { useSiteTheme } from 'src/contexts/SiteTheme/SiteThemeContext';
import { vars } from 'src/core/themes';
import { Colors } from 'src/core/themes/constants/colors';
import { Tooltip } from 'src/core/ui';
import { useGetEventFullInfo } from 'src/hooks/useGetEventFullInfo';
import { useMatchMedia } from 'src/hooks/useMatchMedia';
import { GametimeLogo, StubHubLogo, TickPickLogo, ViagogoLogo } from 'src/svgs';
import { AutomatiqLogo } from 'src/svgs/AutomatiqLogo';
import { AutomatiqSquareLogo } from 'src/svgs/AutomatiqSquareLogo';
import { AxsLogo } from 'src/svgs/AxsLogo';
import { AxsSquareLogo } from 'src/svgs/AxsSquareLogo';
import { ErrorOverlayIcon } from 'src/svgs/ErrorOverlayIcon';
import { GametimeSquareLogo } from 'src/svgs/GametimeSquareLogo';
import { GigsbergLogo } from 'src/svgs/GigsbergLogo';
import { GigsbergSquareLogo } from 'src/svgs/GigsbergSquareLogo';
import { GoTicketsLogo } from 'src/svgs/GoTicketsLogo';
import { GoTicketsSquareLogo } from 'src/svgs/GoTicketsSquareLogo';
import { LyteSquareLogo } from 'src/svgs/LyteSquareLogo';
import { SeatGeekLogo } from 'src/svgs/SeatGeekLogo';
import { SeatGeekSquareLogo } from 'src/svgs/SeatGeekSquareLogo';
import { StubHubSquareLogo } from 'src/svgs/StubHubSquareLogo';
import { TicketEvolutionLogo } from 'src/svgs/TicketEvolutionLogo';
import { TicketEvolutionSquareLogo } from 'src/svgs/TicketEvolutionSquareLogo';
import { TicketmasterLogo } from 'src/svgs/TicketmasterLogo';
import { TicketmasterSquareLogo } from 'src/svgs/TicketmasterSquareLogo';
import { TicketNetworkLogo } from 'src/svgs/TicketNetworkLogo';
import { TicketNetworkSquareLogo } from 'src/svgs/TicketNetworkSquareLogo';
import { TickPickSquareLogo } from 'src/svgs/TickPickSquareLogo';
import { ViagogoSquareLogo } from 'src/svgs/ViagogoSquareLogo';
import { VividSeatsLogo } from 'src/svgs/VividSeatsLogo';
import { VividSeatsSquareLogo } from 'src/svgs/VividSeatsSquareLogo';
import {
  MarketplaceEventIdPlaceholder,
  MarketplaceEventUrlTemplate,
} from 'src/utils/constants/constants';
import { getIsInternationalEvent } from 'src/utils/eventWithDataUtils';
import { getStubhubEventWithPricingUrl } from 'src/utils/inventoryUtils';
import {
  Event,
  Marketplace,
  SellerEventMarketplaceEventInfo,
} from 'src/WebApiController';

import * as styles from './MarketplaceLogo.css';

export interface LogoDimensions {
  width: number | string;
  height: number | string;
}

export const logoAreaDimensions = { height: '30px', width: 'auto' };
export const logoDimensions: Record<Marketplace, LogoDimensions> = {
  [Marketplace.Ticketmaster]: { height: 30, width: 80 },
  [Marketplace.VividSeats]: { height: 30, width: 80 },
  [Marketplace.SeatGeek]: { height: 30, width: 47 },
  [Marketplace.Gametime]: { height: 30, width: 80 },
  [Marketplace.TickPick]: { height: 30, width: 80 },
  [Marketplace.AXS]: { height: 30, width: 80 },
  [Marketplace.Automatiq]: { height: 30, width: 80 },
  [Marketplace.GoTickets]: { height: 30, width: 80 },
  [Marketplace.TicketNetwork]: { height: 30, width: 80 },
  [Marketplace.TicketEvolution]: { height: 30, width: 80 },
  [Marketplace.Lyte]: { height: 30, width: 80 },
  [Marketplace.StubHub]: { height: 30, width: 80 },
  [Marketplace.TicketNetworkMercury]: { height: 30, width: 80 },
  [Marketplace.B2BSales]: { height: 30, width: 80 },
  [Marketplace.FanXchange]: { height: 30, width: 80 },
  [Marketplace.Offline]: { height: 30, width: 80 },
  [Marketplace.Wastage]: { height: 30, width: 80 },
  [Marketplace.ReachPro]: { height: 30, width: 80 },
  [Marketplace.Donation]: { height: 30, width: 80 },
  [Marketplace.Gigsberg]: { height: 30, width: 80 },
  [Marketplace.Retail]: { height: 30, width: 80 },
};

export const createSquareMarketplaceLogos = (isViagogo?: boolean) => ({
  [Marketplace.Ticketmaster]: (props: LogoDimensions) => (
    <TicketmasterSquareLogo {...props} />
  ),
  [Marketplace.StubHub]: (props: LogoDimensions) =>
    isViagogo ? (
      <ViagogoSquareLogo {...(props || logoAreaDimensions)} />
    ) : (
      <StubHubSquareLogo {...(props || logoAreaDimensions)} />
    ),
  [Marketplace.VividSeats]: (props: LogoDimensions) => (
    <VividSeatsSquareLogo {...props} />
  ),
  [Marketplace.SeatGeek]: (props: LogoDimensions) => (
    <SeatGeekSquareLogo {...props} />
  ),
  [Marketplace.Gametime]: (props: LogoDimensions) => (
    <GametimeSquareLogo {...props} />
  ),
  [Marketplace.TickPick]: (props: LogoDimensions) => (
    <TickPickSquareLogo {...props} />
  ),
  [Marketplace.AXS]: (props: LogoDimensions) => <AxsSquareLogo {...props} />,
  [Marketplace.GoTickets]: (props: LogoDimensions) => (
    <GoTicketsSquareLogo {...props} />
  ),
  [Marketplace.TicketNetwork]: (props: LogoDimensions) => (
    <TicketNetworkSquareLogo {...props} />
  ),
  [Marketplace.Automatiq]: (props: LogoDimensions) => (
    <AutomatiqSquareLogo {...props} />
  ),
  [Marketplace.TicketEvolution]: (props: LogoDimensions) => (
    <TicketEvolutionSquareLogo {...props} />
  ),
  [Marketplace.Lyte]: (props: LogoDimensions) => <LyteSquareLogo {...props} />,
  [Marketplace.TicketNetworkMercury]: (props: LogoDimensions) => (
    <UnknownMarketplaceLogo name={Marketplace.TicketNetworkMercury} />
  ),
  [Marketplace.B2BSales]: (props: LogoDimensions) => (
    <UnknownMarketplaceLogo name={Marketplace.B2BSales} />
  ),
  [Marketplace.FanXchange]: (props: LogoDimensions) => (
    <UnknownMarketplaceLogo name={Marketplace.FanXchange} />
  ),
  [Marketplace.Offline]: (props: LogoDimensions) => (
    <UnknownMarketplaceLogo name={Marketplace.Offline} />
  ),
  [Marketplace.Wastage]: (props: LogoDimensions) => (
    <UnknownMarketplaceLogo name={Marketplace.Wastage} />
  ),
  [Marketplace.ReachPro]: (props: LogoDimensions) => (
    <StubHubSquareLogo {...(props || logoAreaDimensions)} />
  ),
  [Marketplace.Donation]: (props: LogoDimensions) => (
    <UnknownMarketplaceLogo name={Marketplace.Donation} />
  ),
  [Marketplace.Gigsberg]: (props: LogoDimensions) => (
    <GigsbergSquareLogo {...props} />
  ),
  [Marketplace.Retail]: (props: LogoDimensions) => (
    <UnknownMarketplaceLogo name={Marketplace.Retail} />
  ),
});

const createDefaultMarketplaceLogos = (
  isDarkMode: boolean,
  isViagogo?: boolean
) => ({
  [Marketplace.Ticketmaster]: (props: LogoDimensions) => (
    <TicketmasterLogo {...props} />
  ),
  [Marketplace.StubHub]: (props: LogoDimensions) =>
    isViagogo ? (
      <ViagogoLogo {...(props || logoAreaDimensions)} />
    ) : (
      <StubHubLogo
        {...(props || logoAreaDimensions)}
        colorMode={isDarkMode ? 'darkBackground' : 'lightBackground'}
      />
    ),
  [Marketplace.VividSeats]: (props: LogoDimensions) => (
    <VividSeatsLogo {...props} />
  ),
  [Marketplace.SeatGeek]: (props: LogoDimensions) => (
    <SeatGeekLogo {...props} />
  ),
  [Marketplace.Gametime]: (props: LogoDimensions) => (
    <GametimeLogo {...props} />
  ),
  [Marketplace.TickPick]: (props: LogoDimensions) => (
    <TickPickLogo {...props} />
  ),
  [Marketplace.AXS]: (props: LogoDimensions) => <AxsLogo {...props} />,
  [Marketplace.Automatiq]: (props: LogoDimensions) => (
    <AutomatiqLogo {...props} />
  ),
  [Marketplace.GoTickets]: (props: LogoDimensions) => (
    <GoTicketsLogo {...props} />
  ),
  [Marketplace.TicketNetwork]: (props: LogoDimensions) => (
    <TicketNetworkLogo {...props} />
  ),
  [Marketplace.Lyte]: (props: LogoDimensions) => (
    <UnknownMarketplaceLogo name={Marketplace.Lyte} />
  ),
  [Marketplace.TicketNetworkMercury]: (props: LogoDimensions) => (
    <UnknownMarketplaceLogo name={Marketplace.TicketNetworkMercury} />
  ),
  [Marketplace.B2BSales]: (props: LogoDimensions) => (
    <UnknownMarketplaceLogo name={Marketplace.B2BSales} />
  ),
  [Marketplace.FanXchange]: (props: LogoDimensions) => (
    <UnknownMarketplaceLogo name={Marketplace.FanXchange} />
  ),
  [Marketplace.Offline]: (props: LogoDimensions) => (
    <UnknownMarketplaceLogo name={Marketplace.Offline} />
  ),
  [Marketplace.TicketEvolution]: (props: LogoDimensions) => (
    <TicketEvolutionLogo
      {...props}
      fillColor={isDarkMode ? Colors.white : undefined}
    />
  ),
  [Marketplace.Wastage]: (props: LogoDimensions) => (
    <UnknownMarketplaceLogo name={Marketplace.Wastage} />
  ),
  [Marketplace.ReachPro]: (props: LogoDimensions) => (
    <StubHubLogo
      {...(props || logoAreaDimensions)}
      colorMode={isDarkMode ? 'darkBackground' : 'lightBackground'}
    />
  ),
  [Marketplace.Donation]: (props: LogoDimensions) => (
    <UnknownMarketplaceLogo name={Marketplace.Donation} />
  ),
  [Marketplace.Gigsberg]: (props: LogoDimensions) => (
    <GigsbergLogo {...props} />
  ),
  [Marketplace.Retail]: (props: LogoDimensions) => (
    <UnknownMarketplaceLogo name={Marketplace.Retail} />
  ),
});

const UnknownMarketplaceLogo = ({ name }: { name?: string }) => (
  <div>{name ?? 'Unknown Marketplace'}</div>
);

export const MarketplaceLogo = ({
  marketplace,
  isViagogo,
  event,
  grayScaleColor,
  marketplaceEventInfo,
  onClick,
  logoDimensions: dimensions,
  variant = 'default',
  showTooltip = false,
  disabled,
  showErrorDotOverlay,
}: {
  marketplace?: Marketplace | null;
  isViagogo?: boolean;
  event?: Event;
  grayScaleColor?: boolean;
  marketplaceEventInfo?: SellerEventMarketplaceEventInfo | null;
  /**
   * Override of the onClick event for the logo. By default, the logo will open the event in the marketplace.
   */
  onClick?: (e: React.MouseEvent<HTMLDivElement>) => void;
  logoDimensions?: LogoDimensions;
  variant?: 'default' | 'square';
  showTooltip?: boolean;
  disabled?: boolean;
  showErrorDotOverlay?: boolean;
}) => {
  const isMobile = useMatchMedia('mobile');
  const { isDarkMode } = useSiteTheme();
  const { venue } = useGetEventFullInfo(event);

  const isInternationalEvent = getIsInternationalEvent(venue?.ctryCode);

  const defaultMarketplaceLogos = useMemo(
    () => createDefaultMarketplaceLogos(isDarkMode, isViagogo),
    [isDarkMode, isViagogo]
  );

  const squareMarketplaceLogos = useMemo(
    () => createSquareMarketplaceLogos(isViagogo),
    [isViagogo]
  );

  const marketplaceLogosByVariant = {
    default: defaultMarketplaceLogos,
    square: squareMarketplaceLogos,
  } as const;

  const logos = marketplaceLogosByVariant[variant];
  const LogoComponent = marketplace ? logos[marketplace] : undefined;

  const onOpenInStubHubClicked = useCallback(() => {
    if (marketplace === Marketplace.StubHub) {
      window.open(
        getStubhubEventWithPricingUrl(event?.viagId, isInternationalEvent),
        isMobile ? '_top' : '_blank'
      );
    } else if (marketplaceEventInfo?.marketplace) {
      let urlTemplate =
        MarketplaceEventUrlTemplate[marketplaceEventInfo.marketplace];

      let urlEventId =
        marketplaceEventInfo.eventIdOverride ??
        marketplaceEventInfo.mappedEventId ??
        '';
      if (marketplace === Marketplace.Ticketmaster) {
        urlEventId = marketplaceEventInfo.websiteEventId ?? urlEventId;
      }
      if (urlEventId) {
        urlTemplate = urlTemplate.replace(
          MarketplaceEventIdPlaceholder,
          urlEventId
        );
        window.open(
          urlTemplate,
          isMobile ? '_top' : '_blank',
          'noopener noreferrer'
        );
      }
    }
  }, [
    event?.viagId,
    isInternationalEvent,
    isMobile,
    marketplace,
    marketplaceEventInfo?.eventIdOverride,
    marketplaceEventInfo?.mappedEventId,
    marketplaceEventInfo?.marketplace,
    marketplaceEventInfo?.websiteEventId,
  ]);

  return (
    <div
      className={clsx(styles.logoContainer, {
        [styles.grayscaleLogo]: grayScaleColor,
        [styles.clickable]: !disabled && (onClick || event),
      })}
      style={
        showErrorDotOverlay
          ? {
              position: 'relative',
            }
          : undefined
      }
      onClick={
        onClick ??
        ((e) => {
          e.preventDefault();
          e.stopPropagation();
          if (event != null) {
            onOpenInStubHubClicked();
          }
        })
      }
    >
      {LogoComponent && marketplace ? (
        showTooltip ? (
          <Tooltip
            contentVariant={isDarkMode ? 'light' : 'darkGray'}
            align="center"
            side="bottom"
            triggerContent={
              <div>
                <LogoComponent
                  {...(dimensions || logoDimensions[marketplace])}
                />
              </div>
            }
          >
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: vars.spacing['xxs'],
                fontSize: vars.typography.fontSize.sm,
                fontWeight: 400,
              }}
            >
              {marketplace}
            </div>
          </Tooltip>
        ) : (
          <LogoComponent {...(dimensions || logoDimensions[marketplace])} />
        )
      ) : (
        <div>{marketplace ?? 'Unknown Marketplace'}</div>
      )}
      {showErrorDotOverlay && (
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: dimensions?.width ?? 24,
            height: dimensions?.height ?? 24,
          }}
        >
          <ErrorOverlayIcon
            height={dimensions?.height ?? 24}
            width={dimensions?.width ?? 24}
          />
        </div>
      )}
    </div>
  );
};
